import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@shared/ui/use-toast";
import {
  notesApi,
  NoteFilters,
  NoteListResponse,
  UpdateNotePayload,
} from "../apis/notes";
import { Note } from "../types/consumerRequest";
import { CreateNoteInput } from "../types/notesTabProps";

export interface UseNotesReturn {
  // Data
  notes: Note[];
  isLoading: boolean;
  isError: boolean;
  error: any;

  // Pagination
  totalCount: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;

  // Mutations
  createNote: (payload: CreateNoteInput) => Promise<void>;

  // Loading states
  isCreating: boolean;
}

export function useNotes(
  filters: NoteFilters,
  options?: {
    enabled?: boolean;
  }
): UseNotesReturn {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);

  // Query for notes list
  const {
    data: notesData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["notes", filters, currentPage],
    queryFn: () => notesApi.getNotes({ ...filters, page: currentPage }),
    enabled: !!filters?.requestId,
  });

  // Create note mutation
  const createMutation = useMutation({
    mutationFn: notesApi.createNote,
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Note created successfully",
        variant: "default",
      });
      queryClient.invalidateQueries({ queryKey: ["notes"] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to create note",
        variant: "destructive",
      });
    },
  });

  // Helper functions
  const createNote = useCallback(
    async (payload: CreateNoteInput) => {
      await createMutation.mutateAsync(payload);
    },
    [createMutation]
  );

  return {
    // Data
    notes: (notesData as NoteListResponse)?.result || [],
    isLoading,
    isError,
    error,

    // Pagination
    totalCount: (notesData as NoteListResponse)?.count || 0,
    hasNextPage: !!(notesData as NoteListResponse)?.next,
    hasPreviousPage: !!(notesData as NoteListResponse)?.previous,

    // Mutations
    createNote,

    // Loading states
    isCreating: createMutation.isPending,
  };
}

// Hook for single note detail
export function useNoteDetail(
  noteId: string,
  options?: {
    enabled?: boolean;
  }
) {
  const {
    data: noteDetail,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["noteDetail", noteId],
    queryFn: () => notesApi.getNoteDetail(noteId),
    enabled: options?.enabled ?? !!noteId,
  });

  return {
    noteDetail: (noteDetail as { result: Note })?.result,
    isLoading,
    isError,
    error,
    refetch,
  };
}

// Hook for note types
export function useNoteTypes() {
  const { data: types, isLoading } = useQuery({
    queryKey: ["noteTypes"],
    queryFn: () => notesApi.getNoteTypes(),
  });

  return {
    types: (types as { result: any[] })?.result || [],
    isLoading,
  };
}

// Hook for notes summary
export function useNotesSummary(params: {
  remoteUtilityId: number;
  requestId?: string;
  consumerId?: string;
}) {
  const { data: summary, isLoading } = useQuery({
    queryKey: ["notesSummary", params],
    queryFn: () => notesApi.getNotesSummary(params),
    enabled: !!params.remoteUtilityId,
  });

  return {
    summary: (summary as { result: any })?.result,
    isLoading,
  };
}
